import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  DateRangePicker,
  RadixTable,
  TColumn,
} from '@features/ui';
import Pagination from '@features/ui/components/pagination';
import { downloadFile, formatDate, DateValueType } from '@lib/utils';
import {
  exportPaNewAccountByDate,
  getPaNewAccountByDate,
} from '@src/services/reports';
import {
  TPaNewAccountByDateRes,
  TPaNewAccountByDateRow,
} from '@src/types/reports';

export const PaNewAccountByDate = () => {
  const [valueStart, setValueStart] = useState<DateValueType>(null);
  const [valueEnd, setValueEnd] = useState<DateValueType>(null);
  const [data, setData] = useState<TPaNewAccountByDateRow[]>([]);
  const [generatedAt, setGeneratedAt] = useState('');
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const pageSize = 20;

  const buildParams = (page = currentPage) => ({
    from_date: valueStart?.startDate
      ? dayjs(valueStart.startDate).format('YYYY-MM-DD')
      : '',
    to_date: valueEnd?.startDate
      ? dayjs(valueEnd.startDate).format('YYYY-MM-DD')
      : '',
    page,
    page_size: pageSize,
  });

  const handleGetData = async (page = 1) => {
    if (!valueStart?.startDate || !valueEnd?.startDate) {
      toast.error('Please select both start and end dates.');

      return;
    }

    setLoading(true);
    try {
      const response: TPaNewAccountByDateRes = await getPaNewAccountByDate(
        buildParams(page)
      );

      setData(response.data);
      setGeneratedAt(response.generated_at);
      setTotalAccounts(response.total_accounts);
      setTotalUsers(response.total_users);
      setCurrentPage(response.page);
      setTotalPages(response.total_pages);
      toast.success('Report generated successfully.');
    } catch (error) {
      console.error('Error fetching PA new accounts by date report:', error);
      toast.error('Failed to fetch report data.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!valueStart?.startDate || !valueEnd?.startDate) {
      toast.error('Please select both start and end dates.');

      return;
    }

    setExporting(true);
    try {
      const response = await exportPaNewAccountByDate(buildParams(1));

      downloadFile(response.data, 'pa-new-accounts-by-date');
      toast.success('Report exported successfully.');
    } catch (error) {
      console.error('Error exporting PA new accounts by date report:', error);
      toast.error('Failed to export report.');
    } finally {
      setExporting(false);
    }
  };

  const dateRangeText = useMemo(() => {
    if (!valueStart?.startDate || !valueEnd?.startDate) {
      return 'Select a date range';
    }

    return `${formatDate(valueStart.startDate.toISOString())} to ${formatDate(valueEnd.startDate.toISOString())}`;
  }, [valueEnd, valueStart]);

  const columns = useMemo<TColumn<TPaNewAccountByDateRow>[]>(
    () => [
      {
        title: 'Name',
        className: 'min-w-[200px]',
        render: (item) => <p>{item?.name || '-'}</p>,
      },
      {
        title: 'Email',
        className: 'min-w-[240px]',
        render: (item) => <p>{item?.email || '-'}</p>,
      },
      {
        title: 'Username',
        className: 'min-w-[160px]',
        render: (item) => <p>{item?.username || '-'}</p>,
      },
      {
        title: 'Account ID',
        className: 'min-w-[180px]',
        render: (item) => <p>{item?.account_name || '-'}</p>,
      },
      {
        title: 'Created On',
        className: 'min-w-[140px]',
        render: (item) => <p>{item?.created_on || '-'}</p>,
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <h1 className="text-[23px]">PA - New Accounts by Date</h1>
          <p className="text-sm text-muted-foreground">{dateRangeText}</p>
          {generatedAt ? (
            <p className="text-xs text-muted-foreground">
              Generated at {generatedAt}
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-3">
          <Button
            disabled={loading}
            variant="outline"
            onClick={() => handleGetData(1)}
          >
            {loading ? 'Loading...' : 'Get Data'}
          </Button>
          <Button disabled={exporting || data.length === 0} onClick={handleExport}>
            {exporting ? 'Exporting...' : 'Export Data'}
          </Button>
        </div>
      </div>

      <Card>
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">From</label>
            <DateRangePicker
              asSingle
              className="max-w-[239px]"
              placeholder="From date"
              size="sm"
              value={valueStart}
              variant="outline"
              onChange={setValueStart}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">To</label>
            <DateRangePicker
              asSingle
              className="max-w-[239px]"
              placeholder="To date"
              size="sm"
              value={valueEnd}
              variant="outline"
              onChange={setValueEnd}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:ml-auto">
            <div className="rounded-md border border-border/60 bg-muted/30 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Users
              </p>
              <p className="mt-1 text-2xl font-semibold">{totalUsers}</p>
            </div>
            <div className="rounded-md border border-border/60 bg-muted/30 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Accounts
              </p>
              <p className="mt-1 text-2xl font-semibold">{totalAccounts}</p>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        {data.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            Run the report to see matching PA accounts.
          </div>
        ) : (
          <>
            <RadixTable columns={columns} data={data} variant="surface" />
            {totalPages > 1 ? (
              <div className="mt-4 flex justify-center">
                <Pagination
                  current={currentPage}
                  total={totalPages}
                  onChange={(page) => handleGetData(page)}
                />
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
};
